import Button from '@components/ui/Button'
import { Wrapper } from '@googlemaps/react-wrapper'
import { RetailerFragment } from '@lib/umbraco/generated/types.generated'
import { selectLabels } from '@redux/globalSlice'
import { isMobile } from '@utils/helpers/DeviceUtils'
import cn from 'clsx'
import { useQueryState } from 'next-usequerystate'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import GoogleMap, { smoothZoom } from './Map/GoogleMap'
import { MapStyles } from './MapStyles/MapStyles'
import RetailerList from './RetailerList/RetailerList'
import RetailerSearch from './RetailerSearch/RetailerSearch'
import styles from './StoreLocator.module.scss'

export type StoreLocatorProps = {
  retailers?: RetailerFragment[]
}

const StoreLocator: FC<StoreLocatorProps> = ({ retailers, ...args }) => {
  const labels = useSelector(selectLabels)
  const [selectedRetailer, setSelectedRetailer] =
    React.useState<RetailerFragment>()
  const [visibleRetailers, setVisibleRetailers] =
    React.useState<RetailerFragment[]>()
  const [map, setMap] = React.useState<google.maps.Map>()
  const [zoom, setZoom] = React.useState(3) // initial zoom
  const [showMap, setShowMap] = React.useState(false)
  const [center, setCenter] = React.useState<google.maps.LatLngLiteral>({
    lat: 0,
    lng: 0,
  })
  const [centerPosition, setCenterPosition] = useState<google.maps.LatLng>()
  const [selectedTags, setSelectedTags] = useQueryState('filterTags')

  // Add this state near the other state declarations
  const [mapInitialized, setMapInitialized] = useState(false)

  // Get unique tags from all retailers
  const allTags = React.useMemo(() => {
    if (!retailers) return []
    const tags = retailers
      .flatMap((r) => r.filterTags ?? [])
      .filter((tag): tag is string => tag !== null)
    return [...new Set(tags)]
  }, [retailers])

  const tagOptions = allTags.map((tag) => ({
    value: tag,
    label: tag,
  }))

  // Filter retailers based on selected tags
  const filteredRetailers = React.useMemo(() => {
    if (!retailers) return []
    if (!selectedTags) return retailers

    const tagList = selectedTags.split(';').filter(Boolean)
    if (tagList.length === 0) return retailers

    return retailers.filter((retailer) => {
      const retailerTags = retailer.filterTags ?? []
      return tagList.some((tag) => retailerTags.includes(tag))
    })
  }, [retailers, selectedTags])

  // Update visible retailers when tags change
  useEffect(() => {
    if (!map) return

    if (selectedTags) {
      // When tags are selected, show all matching retailers
      setVisibleRetailers(filteredRetailers)
    } else {
      // When no tags, show retailers in current view
      const bounds = map.getBounds()
      if (!bounds) return

      const inViewRetailers =
        retailers?.filter((retailer) =>
          bounds.contains(
            new google.maps.LatLng(
              parseFloat(retailer.geoLatitude),
              parseFloat(retailer.geoLongitude),
            ),
          ),
        ) || []
      setVisibleRetailers(inViewRetailers)
    }
  }, [selectedTags, filteredRetailers, map])

  const sidepanel = useRef<HTMLDivElement>(null)
  const onIdle = (m: google.maps.Map) => {
    setZoom(m.getZoom()!)
    setCenter(m.getCenter()!.toJSON())
  }

  useEffect(() => {
    if (navigator.geolocation && map && !mapInitialized) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          smoothZoom(map, 12, map.getZoom(), new google.maps.LatLng(pos))
        },
      )
    }
    if (map && !mapInitialized) {
      setCenterPosition(
        new google.maps.LatLng({
          lat: map.getCenter().lat(),
          lng: map.getCenter().lng(),
        }),
      )
    }
  }, [map, mapInitialized])

  const handleRetailerClicked = (retailer: RetailerFragment) => {
    if (isMobile()) return
    setSelectedRetailer(retailer)
  }

  return (
    <div className={styles.container}>
      <Wrapper
        libraries={['places', 'geometry']}
        apiKey="AIzaSyByDniMRnKk07jfoD5j2MtDkZ6QN0q4HGE"
      >
        <div
          className={cn(styles.sidePanel, showMap && styles.overflowY)}
          ref={sidepanel}
        >
          <h1 className={cn(styles.headline, 'headerTwo')}>Store locator</h1>
          <RetailerSearch
            map={map!}
            searchPlaceholder={labels.labelStoreLocatorSearchPlaceHolder!}
          />
          {tagOptions.length > 0 && (
            <div className={styles.filterContainer}>
              <Select
                isMulti
                options={tagOptions}
                className="retailer-tag-filter"
                placeholder="Filter by tags"
                value={
                  selectedTags
                    ? selectedTags
                        .split(';')
                        .map((tag) => ({ value: tag, label: tag }))
                    : []
                }
                onChange={(newValue) => {
                  const tags = newValue.map((item) => item.value).join(';')
                  setSelectedTags(tags || null)

                  // If removing all tags, refresh the visible retailers based on current map bounds
                  if (!tags && map) {
                    const bounds = map.getBounds()
                    if (bounds) {
                      const inViewRetailers =
                        retailers?.filter((retailer) =>
                          bounds.contains(
                            new google.maps.LatLng(
                              parseFloat(retailer.geoLatitude),
                              parseFloat(retailer.geoLongitude),
                            ),
                          ),
                        ) || []
                      setVisibleRetailers(inViewRetailers)
                    }
                  }
                }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    marginBottom: '1rem',
                    borderLeft: 0,
                    borderRight: 0,
                    borderTop: 0,
                    borderBottom: '1px solid #000',
                    borderRadius: 0,
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#000',
                    },
                    '> div': {
                      padding: 0,
                    },
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? '#f8f7f7'
                      : state.isFocused
                        ? '#f8f7f7'
                        : '#fff',
                    color: '#000',
                    '&:active': {
                      backgroundColor: '#f8f7f7',
                    },
                  }),
                  multiValue: (provided) => ({
                    ...provided,
                    backgroundColor: '#f8f7f7',
                  }),
                  multiValueRemove: (provided) => ({
                    ...provided,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#000',
                    },
                  }),
                }}
              />
            </div>
          )}
          <div className={styles.mobileBtn}>
            <Button
              variant="secondary"
              className=" basis-1/2"
              active={!showMap}
              onClick={() => setShowMap(false)}
            >
              List
            </Button>
            <Button
              variant="secondary"
              className=" basis-1/2"
              active={showMap}
              onClick={() => setShowMap(true)}
            >
              Map
            </Button>
          </div>
          <RetailerList
            centerPosition={centerPosition}
            selectedRetailer={selectedRetailer}
            directionsText={labels.labelStoreLocatorDirections!}
            className={`${showMap && styles.mobileHide}`}
            retailerListHeadline={labels.labelStoreLocatorRetailersHeadline!}
            visibleRetailers={visibleRetailers!}
            onRetailerClick={handleRetailerClicked}
          />
        </div>

        <div
          className={`${styles.mapWrapper} ${!showMap && styles.mobileHide}`}
        >
          <GoogleMap
            center={center}
            onIdle={onIdle}
            zoom={zoom}
            map={map}
            setMap={setMap}
            selectedRetailer={selectedRetailer!}
            setSelectedRetailer={setSelectedRetailer}
            onRetailerClick={handleRetailerClicked}
            style={{ flexGrow: '1', height: '100%' }}
            mapTypeControl={false}
            setVisibleRetailers={(retailers: RetailerFragment[]) => {
              if (!map) return
              const bounds = map.getBounds()
              if (!bounds) return

              // If there are filters active, show all filtered retailers
              // If no filters, only show retailers in the current map view
              if (selectedTags) {
                setVisibleRetailers(filteredRetailers)
              } else {
                // Show retailers that are currently in view
                const inViewRetailers = retailers.filter((retailer) =>
                  bounds.contains(
                    new google.maps.LatLng(
                      parseFloat(retailer.geoLatitude),
                      parseFloat(retailer.geoLongitude),
                    ),
                  ),
                )
                setVisibleRetailers(inViewRetailers)
              }
            }}
            fullscreenControl={false}
            streetViewControl={false}
            retailers={filteredRetailers}
            styles={MapStyles}
          />
        </div>
      </Wrapper>
    </div>
  )
}

export default StoreLocator
